import React, { useState, useEffect } from 'react';
import { 
  Search, RefreshCw, ChevronRight, CheckCircle, XCircle, 
  Clock, AlertTriangle, Phone, User, Building, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OpsClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // QA Modal state
  const [showQAModal, setShowQAModal] = useState(false);
  const [qaForm, setQaForm] = useState({
    test_call_id: '',
    recording_url: '',
    summary_received: false,
    reason: '',
    notes: '',
    checklist: [
      { item: 'call_connects', label: 'Call connects successfully', passed: false },
      { item: 'voice_quality', label: 'Voice quality acceptable', passed: false },
      { item: 'correct_greeting', label: 'Correct greeting & business name', passed: false },
      { item: 'intent_handling', label: 'At least one intent handled correctly', passed: false },
      { item: 'lead_or_booking', label: 'Lead captured OR booking flow triggered', passed: false },
      { item: 'recording_received', label: 'Recording + summary received', passed: false },
    ]
  });

  const token = localStorage.getItem('ops_token');

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, searchTerm, statusFilter]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ops/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    let filtered = [...clients];
    
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.industry?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.onboarding_status === statusFilter);
    }
    
    setFilteredClients(filtered);
  };

  const selectClient = async (client) => {
    setSelectedClient(client);
    
    try {
      const res = await fetch(`${API_URL}/api/ops/clients/${client.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClientDetail(data);
      }
    } catch (err) {
      console.error('Error fetching client detail:', err);
    }
  };

  const markSetupFeePaid = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ops/clients/${selectedClient.id}/setup-fee`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        alert('Setup fee marked as paid!');
        fetchClients();
        selectClient(selectedClient);
      } else {
        const err = await res.json();
        alert(`Error: ${err.detail || 'Failed to update'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/ops/clients/${selectedClient.id}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        alert(`Status updated to ${newStatus}!`);
        fetchClients();
        selectClient(selectedClient);
      } else {
        const err = await res.json();
        alert(`Error: ${err.detail || 'Failed to update'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleQAPass = async () => {
    const allPassed = qaForm.checklist.every(item => item.passed);
    if (!allPassed) {
      alert('All checklist items must be passed');
      return;
    }
    if (!qaForm.test_call_id) {
      alert('Test Call ID is required');
      return;
    }
    if (!qaForm.summary_received) {
      alert('Summary received must be checked');
      return;
    }
    if (!qaForm.reason) {
      alert('Reason is required');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/ops/clients/${selectedClient.id}/qa-pass`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          test_call_id: qaForm.test_call_id,
          recording_url: qaForm.recording_url,
          summary_received: qaForm.summary_received,
          checklist: qaForm.checklist,
          reason: qaForm.reason,
          notes: qaForm.notes
        })
      });

      if (res.ok) {
        alert('QA Passed! Client can now be activated.');
        setShowQAModal(false);
        fetchClients();
        selectClient(selectedClient);
      } else {
        const err = await res.json();
        alert(`Error: ${typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail)}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const activateClient = async () => {
    if (!window.confirm('Are you sure you want to activate this client? They will go LIVE.')) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/ops/clients/${selectedClient.id}/activate?reason=Onboarding complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        alert('🎉 Client activated successfully! They are now LIVE.');
        fetchClients();
        selectClient(selectedClient);
      } else {
        const err = await res.json();
        const detail = err.detail;
        if (typeof detail === 'object' && detail.issues) {
          alert(`Cannot activate:\n• ${detail.issues.join('\n• ')}`);
        } else {
          alert(`Error: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
        }
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_setup: 'bg-yellow-100 text-yellow-700',
      in_review: 'bg-blue-100 text-blue-700',
      live: 'bg-green-100 text-green-700',
      suspended: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getQABadge = (status) => {
    if (status === 'passed') return 'bg-green-100 text-green-700';
    if (status === 'failed') return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="h-screen flex">
      {/* Client List */}
      <div className="w-1/2 border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900">Clients</h1>
            <button 
              onClick={fetchClients}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          
          {/* Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="all">All Status</option>
            <option value="pending_setup">Pending Setup</option>
            <option value="in_review">In Review</option>
            <option value="live">Live</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        {/* Client List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredClients.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No clients found</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredClients.map(client => (
                <div
                  key={client.id}
                  onClick={() => selectClient(client)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
                    selectedClient?.id === client.id ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''
                  }`}
                >
                  <div>
                    <p className="font-medium text-gray-900">{client.name}</p>
                    <p className="text-sm text-gray-500">{client.industry} • {client.plan_id || 'No plan'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(client.onboarding_status)}`}>
                      {client.onboarding_status}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Client Detail */}
      <div className="w-1/2 bg-gray-50 overflow-y-auto">
        {!selectedClient ? (
          <div className="h-full flex items-center justify-center text-gray-500">
            <div className="text-center">
              <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Select a client to view details</p>
            </div>
          </div>
        ) : (
          <div className="p-6">
            {/* Client Header */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedClient.name}</h2>
                  <p className="text-gray-500">{selectedClient.industry}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(selectedClient.onboarding_status)}`}>
                  {selectedClient.onboarding_status}
                </span>
              </div>

              {/* Status Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Setup Fee</p>
                  <p className={`font-semibold ${selectedClient.setup_fee_paid || selectedClient.setup_fee_status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>
                    {selectedClient.setup_fee_paid || selectedClient.setup_fee_status === 'paid' ? '✓ Paid' : '○ Unpaid'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">QA Status</p>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${getQABadge(selectedClient.qa_status)}`}>
                    {selectedClient.qa_status || 'pending'}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Plan</p>
                  <p className="font-semibold text-gray-900">{selectedClient.plan_id || 'Not set'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Agents Linked</p>
                  <p className="font-semibold text-gray-900">{clientDetail?.agents?.length || 0}</p>
                </div>
              </div>
            </div>

            {/* Onboarding Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">Onboarding Actions</h3>
              
              <div className="space-y-3">
                {/* Step 1: Setup Fee */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedClient.setup_fee_paid || selectedClient.setup_fee_status === 'paid' ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Setup Fee</span>
                  </div>
                  {!(selectedClient.setup_fee_paid || selectedClient.setup_fee_status === 'paid') && (
                    <button
                      onClick={markSetupFeePaid}
                      className="px-3 py-1 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
                    >
                      Mark Paid
                    </button>
                  )}
                </div>

                {/* Step 2: Move to In Review */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedClient.onboarding_status !== 'pending_setup' ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Status: In Review</span>
                  </div>
                  {selectedClient.onboarding_status === 'pending_setup' && (
                    <button
                      onClick={() => updateStatus('in_review')}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                    >
                      Move to Review
                    </button>
                  )}
                </div>

                {/* Step 3: Agent Linked */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {(clientDetail?.agents?.length || 0) > 0 ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Agent Linked ({clientDetail?.agents?.length || 0})</span>
                  </div>
                  <a
                    href="/ops/link-agent"
                    className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                  >
                    Link Agent
                  </a>
                </div>

                {/* Step 4: QA */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedClient.qa_status === 'passed' ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : selectedClient.qa_status === 'failed' ? (
                      <XCircle className="w-5 h-5 text-red-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-500" />
                    )}
                    <span>QA Checklist</span>
                  </div>
                  {selectedClient.qa_status !== 'passed' && (
                    <button
                      onClick={() => setShowQAModal(true)}
                      className="px-3 py-1 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600"
                    >
                      Run QA
                    </button>
                  )}
                </div>

                {/* Step 5: Activate */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedClient.onboarding_status === 'live' ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Zap className="w-5 h-5 text-gray-400" />
                    )}
                    <span>Activate Client</span>
                  </div>
                  {selectedClient.onboarding_status !== 'live' && (
                    <button
                      onClick={activateClient}
                      disabled={selectedClient.qa_status !== 'passed'}
                      className={`px-3 py-1 text-sm rounded-lg ${
                        selectedClient.qa_status === 'passed'
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Activate
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Agents */}
            {clientDetail?.agents?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">Linked Agents</h3>
                <div className="space-y-2">
                  {clientDetail.agents.map(agent => (
                    <div key={agent.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{agent.agent_id}</p>
                        <p className="text-sm text-gray-500">{agent.sip_number}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        agent.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {agent.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Calls */}
            {clientDetail?.recent_calls?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Recent Calls</h3>
                <div className="space-y-2">
                  {clientDetail.recent_calls.slice(0, 5).map(call => (
                    <div key={call.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{call.from_number || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{call.duration_minutes || 0} min • {call.intent || 'Unknown'}</p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(call.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* QA Modal */}
      {showQAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">QA Checklist</h2>
              <p className="text-gray-500">{selectedClient?.name}</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Checklist Items */}
              <div className="space-y-3">
                {qaForm.checklist.map((item, idx) => (
                  <label 
                    key={item.item}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                  >
                    <input
                      type="checkbox"
                      checked={item.passed}
                      onChange={(e) => {
                        const newChecklist = [...qaForm.checklist];
                        newChecklist[idx].passed = e.target.checked;
                        setQaForm({ ...qaForm, checklist: newChecklist });
                      }}
                      className="w-5 h-5 text-emerald-600 rounded"
                    />
                    <span className="text-gray-700">{item.label}</span>
                  </label>
                ))}
              </div>

              {/* Proof Fields */}
              <div className="pt-4 border-t border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Call ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={qaForm.test_call_id}
                    onChange={(e) => setQaForm({ ...qaForm, test_call_id: e.target.value })}
                    placeholder="call_test_001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recording URL
                  </label>
                  <input
                    type="text"
                    value={qaForm.recording_url}
                    onChange={(e) => setQaForm({ ...qaForm, recording_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={qaForm.summary_received}
                    onChange={(e) => setQaForm({ ...qaForm, summary_received: e.target.checked })}
                    className="w-5 h-5 text-emerald-600 rounded"
                  />
                  <span className="text-gray-700">Summary received in dashboard</span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={qaForm.reason}
                    onChange={(e) => setQaForm({ ...qaForm, reason: e.target.value })}
                    placeholder="QA verification complete"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={qaForm.notes}
                    onChange={(e) => setQaForm({ ...qaForm, notes: e.target.value })}
                    rows={2}
                    placeholder="Optional notes..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowQAModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleQAPass}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Mark Passed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
